import axios from 'axios'
import { jwtDecode } from 'jwt-decode'
import { DecodedToken } from '../types/auth.types'
import { useAuthStore } from './useAuthStore'

const axiosClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
})

axiosClient.interceptors.request.use(
  config => {
    const { idToken } = useAuthStore.getState()
    if (idToken) {
      config.headers['Authorization'] = `Bearer ${idToken}`
    }
    return config
  },
  error => {
    return Promise.reject(error)
  }
)

axiosClient.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config

    if (originalRequest._retry) {
      return Promise.reject(error)
    }

    const { idToken, refreshTokens } = useAuthStore.getState()
    if (!idToken) {
      return Promise.reject(error)
    }

    const { reason, details } = error.response?.data?.data || {}

    const isAccessTokenExpired =
      reason === 'NotAuthorizedException' &&
      details?.message === 'Access Token has expired'

    const decoded = jwtDecode<DecodedToken>(idToken)
    const now = Date.now() / 1000
    const isIdTokenExpired = decoded.exp && decoded.exp < now

    if (!isAccessTokenExpired && !isIdTokenExpired) {
      return Promise.reject(error)
    }

    originalRequest._retry = true

    try {
      await refreshTokens()
      const { idToken: newIdToken, accessToken } = useAuthStore.getState()

      if (isAccessTokenExpired) {
        originalRequest.data = JSON.stringify({ accessToken })
      }

      originalRequest.headers['Authorization'] = `Bearer ${newIdToken}`
      return axiosClient(originalRequest)
    } catch (refreshError) {
      return Promise.reject(refreshError)
    }
  }
)

export default axiosClient
